const MILESTONES_POSITION = 1000000

export function randomPosition(min, max) {
    return (max + min) / 2 + (Math.ceil(Math.random() * 1000) * (max - min)) / 100000
}

export function randomPositionFirstItem() {
    return Math.ceil((MILESTONES_POSITION / 2) * (Math.random() + 1))
}

export function getPositionMoveInObject(sortedArray, sortKey, oldIndex, newIndex, sortByAsc = false) {
    let position = 0
    if (!newIndex) {
        position = sortedArray[0][sortKey] + randomPositionFirstItem() * (sortByAsc ? -1 : 1)
    } else if (newIndex === sortedArray.length - 1) {
        position = sortedArray[sortedArray.length - 1][sortKey] + randomPositionFirstItem() * (sortByAsc ? 1 : -1)
    } else if (newIndex < oldIndex) {
        const start = sortedArray[newIndex - 1][sortKey]
        const end = sortedArray[newIndex][sortKey]
        position = randomPosition(start, end)
    } else if (newIndex > oldIndex) {
        const start = sortedArray[newIndex + 1][sortKey]
        const end = sortedArray[newIndex][sortKey]
        position = randomPosition(start, end)
    }
    if (isNaN(position)) return randomPositionFirstItem()
    return position
}

const POSITION_GAP = 1000

function numericPosition(position: number | null | undefined): number | null {
    if (typeof position === "number" && !Number.isNaN(position)) {
        return position
    }
    return null
}

/** Keep the current order as descending positions, filling any missing values. */
export function withDescendingPositions<T extends {position?: number | null}>(items: T[]): T[] {
    const filled: Array<number | null> = items.map((item) => numericPosition(item.position))
    if (filled.every((position) => position === null)) {
        return items.map((item, index) => ({
            ...item,
            position: (items.length - index) * POSITION_GAP
        }))
    }

    for (let index = 0; index < filled.length; index++) {
        if (filled[index] !== null) {
            continue
        }
        let previous = index - 1
        while (previous >= 0 && filled[previous] === null) {
            previous -= 1
        }
        let next = index + 1
        while (next < filled.length && filled[next] === null) {
            next += 1
        }
        if (previous >= 0 && next < filled.length) {
            const start = filled[previous] as number
            const end = filled[next] as number
            const step = (end - start) / (next - previous)
            filled[index] = start + step * (index - previous)
        } else if (previous >= 0) {
            filled[index] = (filled[previous] as number) - POSITION_GAP * (index - previous)
        } else if (next < filled.length) {
            filled[index] = (filled[next] as number) + POSITION_GAP * (next - index)
        }
    }

    return items.map((item, index) => ({
        ...item,
        position: filled[index] as number
    }))
}

export function reorderDescendingByPosition<T extends {position?: number | null}>(
    items: T[],
    sourceIndex: number,
    destinationIndex: number
): T[] {
    const seeded = withDescendingPositions(items)
    const newPosition = getPositionMoveInObject(seeded, "position", sourceIndex, destinationIndex)
    const next = [...seeded]
    const [removed] = next.splice(sourceIndex, 1)
    next.splice(destinationIndex, 0, {...removed, position: newPosition})
    return next
}

export function getPositionMoveToObject(sortedArray, sortKey, newIndex) {
    if (!sortedArray.length) return randomPositionFirstItem()
    let position = 0
    if (!newIndex) {
        position = sortedArray[0][sortKey] + randomPositionFirstItem()
    } else if (newIndex === sortedArray.length) {
        position = sortedArray[sortedArray.length - 1][sortKey] - randomPositionFirstItem()
    } else {
        const start = sortedArray[newIndex - 1][sortKey]
        const end = sortedArray[newIndex][sortKey]
        position = randomPosition(start, end)
    }

    if (isNaN(position)) return randomPositionFirstItem()

    return position
}
